/**
 * ProgressChart - A simple SVG-based line chart for workout progress
 * No external dependencies, lightweight and responsive
 */

import { useMemo } from 'react';

export interface DataPoint {
  date: Date;
  value: number;
  label?: string;
}

interface ProgressChartProps {
  data: DataPoint[];
  height?: number;
  color?: string;
  showDots?: boolean;
  showArea?: boolean;
  formatValue?: (value: number) => string;
  formatDate?: (date: Date) => string;
  title?: string;
  unit?: string;
}

export function ProgressChart({
  data,
  height = 200,
  color = '#10b981', // emerald-500
  showDots = true,
  showArea = true,
  formatValue = (v) => v.toString(),
  formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  title,
  unit,
}: ProgressChartProps) {
  const chartData = useMemo(() => {
    if (data.length === 0) return null;

    // Sort by date
    const sorted = [...data].sort((a, b) => a.date.getTime() - b.date.getTime());

    // Calculate bounds
    const values = sorted.map((d) => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const valueRange = maxValue - minValue || 1;

    // Add padding to value range
    const paddedMin = minValue - valueRange * 0.1;
    const paddedMax = maxValue + valueRange * 0.1;
    const paddedRange = paddedMax - paddedMin;

    // Chart dimensions
    const width = 100; // percentage
    const padding = { top: 10, right: 10, bottom: 30, left: 40 };
    const chartWidth = width;
    const chartHeight = height - padding.top - padding.bottom;

    // Generate points
    const points = sorted.map((d, i) => {
      const x = padding.left + (i / (sorted.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
      const y = padding.top + (1 - (d.value - paddedMin) / paddedRange) * chartHeight;
      return { x, y, data: d };
    });

    // Generate SVG path
    const linePath = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
      .join(' ');

    // Generate area path (for fill under line)
    const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padding.bottom} L ${points[0].x} ${height - padding.bottom} Z`;

    return {
      points,
      linePath,
      areaPath,
      minValue: paddedMin,
      maxValue: paddedMax,
      latest: sorted[sorted.length - 1],
      first: sorted[0],
      trend: sorted.length > 1 ? sorted[sorted.length - 1].value - sorted[0].value : 0,
    };
  }, [data, height]);

  if (!chartData || data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center bg-gray-50 dark:bg-white/5 rounded-lg"
        style={{ height }}
      >
        <p className="text-gray-500 dark:text-slate-500 text-sm">No data yet</p>
      </div>
    );
  }

  const { points, linePath, areaPath, trend, latest } = chartData;

  return (
    <div className="w-full">
      {/* Header */}
      {(title || unit) && (
        <div className="flex items-baseline justify-between mb-2">
          {title && (
            <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300">
              {title}
            </h4>
          )}
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatValue(latest.value)}
            </span>
            {unit && (
              <span className="text-sm text-gray-500 dark:text-slate-500">{unit}</span>
            )}
            {data.length > 1 && (
              <span className={`text-sm font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend >= 0 ? '↑' : '↓'} {formatValue(Math.abs(trend))}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Chart */}
      <svg
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height }}
      >
        {/* Area fill */}
        {showArea && (
          <path
            d={areaPath}
            fill={color}
            fillOpacity={0.1}
          />
        )}

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* Dots */}
        {showDots && points.map((point, i) => (
          <g key={i}>
            <circle
              cx={point.x}
              cy={point.y}
              r={4}
              fill="white"
              stroke={color}
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
            {/* Show value on hover - using CSS */}
            <title>{`${formatDate(point.data.date)}: ${formatValue(point.data.value)}${unit ? ` ${unit}` : ''}`}</title>
          </g>
        ))}

        {/* X-axis labels */}
        {points.length > 0 && (
          <>
            <text
              x={points[0].x}
              y={height - 5}
              fontSize={8}
              fill="currentColor"
              className="text-gray-500 dark:text-slate-500"
              textAnchor="start"
            >
              {formatDate(points[0].data.date)}
            </text>
            {points.length > 1 && (
              <text
                x={points[points.length - 1].x}
                y={height - 5}
                fontSize={8}
                fill="currentColor"
                className="text-gray-500 dark:text-slate-500"
                textAnchor="end"
              >
                {formatDate(points[points.length - 1].data.date)}
              </text>
            )}
          </>
        )}
      </svg>
    </div>
  );
}

/**
 * Compact sparkline version for inline use
 */
interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
}

export function Sparkline({
  data,
  width = 80,
  height = 24,
  color = '#10b981',
}: SparklineProps) {
  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="inline-block">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default ProgressChart;
